import {
  cleaningsTabFilterOptions,
  specialProjectsTabFilterOptions,
} from "../../classes/task/task-strings";
import brand from "./brand";

import i18n from "../../i18n/config";

export type NavChild = {
  name: string;
  href: string;
  current: boolean;
};

export type NavParent = {
  name: string;
  href: string;
  current: boolean;
  icon?: string;
  children?: NavChild[];
};

export const cleansSubtypeOptions = cleaningsTabFilterOptions
  .map((subType) => subType.replace(" & ", " And "))
  .join(",");
export const specialSubtypeOptions = specialProjectsTabFilterOptions
  .map((subType) => subType.replace(" & ", " And "))
  .join(",");
const deliveryOptions = ["Drop", "Pickup"].join(",");
// categories
export const MY_TASKS = "my-tasks";
export const ALL_TASKS = "all";
export const UNASSIGNED_TASKS = "unassigned";
export const OVERDUE_TASKS = "overdue";

// filter presets
export const DROP_STRIPS_TASKS = "DropStrips";
export const GLASS_LANAI_TASKS = "GlassLanai";
export const MAINTENANCE_INSPECTION_TASKS = "MaintenanceInspections";
export const CLEANING_TASKS = "Cleanings";
export const FINAL_INSPECTION_TASKS = "FinalInspections";
export const SPECIAL_PROJECT_TASKS = "SpecialProjects";
export const DELIVERY_TASKS = "Deliveries";
export const SHUTTLE_SERVICE_TASKS = "ShuttleService";

// group membership driven - lowercase route params
export const MAINTENANCE_TASKS = "maintenance";
export const HOUSEKEEPING_TASKS = "housekeeping";
export const GUEST_EXPERIENCE_TASKS = "guest-experience";
export const OWNER_EXPERIENCE_TASKS = "owner-experience";

// Maps lowercase route params to Salesforce queue names
export const QUEUE_ROUTE_TO_SF_NAME: Record<string, string> = {
  "maintenance": "Maintenance",
  "housekeeping": "Housekeeping",
  "guest-experience": "Guest Experience",
  "owner-experience": "Owner Experience",
};

// Legacy route redirects (capitalized → lowercase)
export const LEGACY_QUEUE_REDIRECTS: Record<string, string> = {
  "Engineering": "maintenance",
  "Housekeeping": "housekeeping",
  "GuestExperience": "guest-experience",
  "OwnerExperience": "owner-experience",
};

export enum TaskSearchKey {
  Assignee = "assignee",
  Listing = "listing",
  Resort = "resort",
  Building = "building",
  Status = "status",
  Type = "type",
  SubType = "subtype",
  Priority = "priority",
  Sort = "sortBy",
  View = "filterBy",
  StartDate = "startDate",
  EndDate = "endDate",
}
export const TasksPageParams = {
  All: "allTasks",
  My: "assignedTasks",
  Unassigned: "unassignedTasks",
  Overdue: "overdueTasks",
};

export const PATH_BRAND_TASKS = {
  MY_TASKS: "/tasks/" + MY_TASKS,
  ALL_TASKS: "/tasks/" + ALL_TASKS,

  DROP_STRIPS:
    "/tasks/" +
    DROP_STRIPS_TASKS +
    `?${TaskSearchKey.Type}=Clean&subtype=Drop %26 Strip`, // This %26 is an ampersand, i.e "Drop & Strip".
  MAINTENANCE_INSPECTION:
    "/tasks/" +
    MAINTENANCE_INSPECTION_TASKS +
    `?${TaskSearchKey.Type}=Inspection&subtype=Maintenance`,
  GLASS_LANAI:
    "/tasks/" +
    GLASS_LANAI_TASKS +
    `?${TaskSearchKey.Type}=Clean&subtype=Glass %26 Lanai`, // This %26 is an ampersand, i.e "Glass & Lanai".
  FINAL_INSPECTION:
    "/tasks/" +
    FINAL_INSPECTION_TASKS +
    `?${TaskSearchKey.Type}=Inspection&subtype=Final`,
  CLEANINGS:
    "/tasks/" +
    CLEANING_TASKS +
    `?${TaskSearchKey.Type}=Clean&subtype=` +
    encodeURIComponent(cleansSubtypeOptions),
  SPECIAL_PROJECTS:
    "/tasks/" +
    SPECIAL_PROJECT_TASKS +
    `?${TaskSearchKey.Type}=Clean&subtype=` +
    encodeURIComponent(specialSubtypeOptions),
  DELIVERIES:
    "/tasks/" +
    DELIVERY_TASKS +
    `?${TaskSearchKey.Type}=` +
    encodeURIComponent(deliveryOptions),
  SHUTTLE_SERVICE:
    "/tasks/" +
    SHUTTLE_SERVICE_TASKS +
    `?${TaskSearchKey.Type}=Shuttle Service`,

  UNASSIGNED: "/tasks/" + UNASSIGNED_TASKS,
  OVERDUE: "/tasks/" + OVERDUE_TASKS,

  MAINTENANCE: "/tasks/" + MAINTENANCE_TASKS,
  HOUSEKEEPING: "/tasks/" + HOUSEKEEPING_TASKS,
  GUEST_EXPERIENCE: "/tasks/" + GUEST_EXPERIENCE_TASKS,
  OWNER_EXPERIENCE: "/tasks/" + OWNER_EXPERIENCE_TASKS,
};

export const PATH_NOTIFICATIONS = {
  ROOT: "/notifications",
};

export const PATH_RESERVATIONS = {
  ROOT: "/reservations",
  ARRIVALS_TODAY:
    "/reservations/arrivals?hide_departures=true&hide_in_house=true",
  DEPARTURES_TODAY:
    "/reservations/departures?hide_arrivals=true&hide_in_house=true",
  OWNER_ARRIVALS:
    "/reservations/owner_arrivals?hide_departures=true&hide_in_house=true&type=Guest+of+Owner%2COwner",
  CHECK_IN_REQUESTS: "/reservations/check_in_requests?check_in_requests=true",
};

export const navigationByBrand: (brand: BrandConfig) => NavParent[] = () => {
  //NOTE: Order is important here! This sets the menu order.
  // Additions to the brand.listViews should be added here and in config.
  const NavLinks = [];
  if (brand.listTaskViews.includes(MY_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_my_tasks"),
      href: PATH_BRAND_TASKS.MY_TASKS,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(ALL_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_all_tasks"),
      href: PATH_BRAND_TASKS.ALL_TASKS,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(DROP_STRIPS_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_drop_strips"),
      href: PATH_BRAND_TASKS.DROP_STRIPS,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(MAINTENANCE_INSPECTION_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_maintenance_inspections"),
      href: PATH_BRAND_TASKS.MAINTENANCE_INSPECTION,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(CLEANING_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_cleanings"),
      href: PATH_BRAND_TASKS.CLEANINGS,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(GLASS_LANAI_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_glass_and_lanai"),
      href: PATH_BRAND_TASKS.GLASS_LANAI,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(FINAL_INSPECTION_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_final_inspections"),
      href: PATH_BRAND_TASKS.FINAL_INSPECTION,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(SPECIAL_PROJECT_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_special_projects"),
      href: PATH_BRAND_TASKS.SPECIAL_PROJECTS,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(DELIVERY_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_deliveries"),
      href: PATH_BRAND_TASKS.DELIVERIES,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(SHUTTLE_SERVICE_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_shuttle_service"),
      href: PATH_BRAND_TASKS.SHUTTLE_SERVICE,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(MAINTENANCE_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_maintenance"),
      href: PATH_BRAND_TASKS.MAINTENANCE,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(HOUSEKEEPING_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_housekeeping"),
      href: PATH_BRAND_TASKS.HOUSEKEEPING,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(GUEST_EXPERIENCE_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_guest_experience"),
      href: PATH_BRAND_TASKS.GUEST_EXPERIENCE,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(OWNER_EXPERIENCE_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_owner_experience"),
      href: PATH_BRAND_TASKS.OWNER_EXPERIENCE,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(OVERDUE_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_overdue"),
      href: PATH_BRAND_TASKS.OVERDUE,
      current: false,
    });
  }
  if (brand.listTaskViews.includes(UNASSIGNED_TASKS)) {
    NavLinks.push({
      name: i18n.t("nav_menu_tasks_unassigned"),
      href: PATH_BRAND_TASKS.UNASSIGNED,
      current: false,
    });
  }
  return [
    {
      name: i18n.t("nav_menu_dashboard"),
      href: "/home",
      icon: "icon-[ion--home]",
      current: true,
    },
    {
      name: i18n.t("nav_menu_listings"),
      icon: "icon-[icon-park-twotone--hotel]",
      href: "/listings",
      current: false,
    },
    {
      name: i18n.t("nav_menu_notifications"),
      icon: "icon-[ic--round-notifications]",
      href: PATH_NOTIFICATIONS.ROOT,
      current: false,
    },
    {
      name: i18n.t("nav_menu_reservations"),
      icon: "icon-[mdi--calendar]",
      href: PATH_RESERVATIONS.ROOT,
      current: false,
      children: [
        {
          name: i18n.t("nav_menu_reservations_daily_timeline"),
          href: PATH_RESERVATIONS.ROOT,
          current: false,
        },
        {
          name: i18n.t("nav_menu_reservations_arrivals"),
          href: PATH_RESERVATIONS.ARRIVALS_TODAY,
          current: false,
        },
        {
          name: i18n.t("nav_menu_reservations_departures"),
          href: PATH_RESERVATIONS.DEPARTURES_TODAY,
          current: false,
        },
        {
          name: i18n.t("nav_menu_reservations_owner_arrivals"),
          href: PATH_RESERVATIONS.OWNER_ARRIVALS,
          current: false,
        },
        {
          name: i18n.t("nav_menu_reservations_check_in_requests"),
          href: PATH_RESERVATIONS.CHECK_IN_REQUESTS,
          current: false,
        },
      ],
    },
    {
      name: i18n.t("nav_menu_tasks"),
      icon: "icon-[clarity--tasks-outline-badged]",
      href: PATH_BRAND_TASKS.MY_TASKS,
      current: false,
      children: NavLinks,
    },
  ];
};
